import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "../../../Components/Buttons";
import { FormInput, FormSection } from "../../../Components/Form";
import RichTextEditor from "../../../Components/RichTextEditor";
import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";

const EMPTY = { name: "", from: "", subject: "", body: "" };

const CreateEmailTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const details = location?.state?.template;

  const [formData, setFormData] = useState({
    ...EMPTY,
    ...(details ? {
      name: details.name || "",
      from: details.from || "",
      subject: details.subject || "",
      body: details.body || "",
    } : {}),
  });

  const [loading, setLoading] = useState(false);
  const setField = (key, val) => setFormData((p) => ({ ...p, [key]: val }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim()) {
      ToastMessage({ type: "error", title: "Required", message: "Subject is required." });
      return;
    }
    if (!formData.body || formData.body === "<p></p>") {
      ToastMessage({ type: "error", title: "Required", message: "Body is required." });
      return;
    }

    setLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 600));
      ToastMessage({
        type: "success",
        title: details ? "Template updated" : "Template created",
        message: details ? "Updated successfully!" : "Template created successfully!",
      });
      navigate(ROUTE_PATHS.EMAIL_TEMPLATES);
    } catch {
      ToastMessage({ type: "error", title: "Error", message: "Something went wrong." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb title={details ? "Edit Template" : "Create Template"} />

      <form onSubmit={onSubmit} className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
        <FormSection title="Template">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              isRequired
              label="Template Name"
              name="name"
              value={formData.name}
              onChange={(val) => setField("name", val)}
              placeholder="e.g. Welcome Email"
            />
            <FormInput
              label="From Email"
              name="from"
              value={formData.from}
              onChange={(val) => setField("from", val)}
              placeholder="noreply@example.com or {{from_email}}"
            />
            <div className="md:col-span-2">
              <FormInput
                isRequired
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={(val) => setField("subject", val)}
                placeholder="Email subject line..."
              />
            </div>
          </div>
        </FormSection>

        <FormSection title="Body">
          <RichTextEditor
            value={formData.body}
            onChange={(html) => setField("body", html)}
            placeholder="Compose your email template here..."
            minHeight={360}
          />
          <p className="text-xs text-gray-400 mt-2">
            Use <code className="bg-gray-100 dark:bg-slate-700 px-1 rounded">{"{{name}}"}</code> syntax for dynamic placeholders.
          </p>
        </FormSection>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            onClick={() => navigate(ROUTE_PATHS.EMAIL_TEMPLATES)}
            className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="!bg-green-500 !text-white hover:!bg-green-600"
            loading={loading}
            disabled={loading}
            loadingText={details ? "Updating..." : "Creating..."}
          >
            {details ? "Update Template" : "Create Template"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateEmailTemplate;
